// src/components/dashboard/LeadReviewerPanel.jsx
//
// Lead Reviewer triage panel.
// Shows ALL unassigned + assigned covers and drafts pending review.
// Allows assigning each item to a specific reviewer.
// Supports Cards view and Table view with search + filters.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Typography, CircularProgress, Modal, Box, TextField, Button,
  Select, MenuItem, FormControl, InputLabel, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import DashboardCard from './DashboardCard';
import CoverCard from './CoverCard';
import '../../styles/CoverCard.css';
import { API_BASE } from '../../utils/api';

const LeadReviewerPanel = () => {
  const accessToken = localStorage.getItem('rv_access_token');

  const [pendingBooks, setPendingBooks] = useState([]);
  const [pendingCovers, setPendingCovers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

  // Cover preview modal
  const [selectedCover, setSelectedCover] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Search + filter state — books
  const [bookSearch, setBookSearch] = useState('');
  const [bookAuthorFilter, setBookAuthorFilter] = useState('All');
  const [bookReviewerFilter, setBookReviewerFilter] = useState('All');
  const [bookStatusFilter, setBookStatusFilter] = useState('All');

  // Search + filter state — covers
  const [coverSearch, setCoverSearch] = useState('');
  const [coverDesignerFilter, setCoverDesignerFilter] = useState('All');
  const [coverReviewerFilter, setCoverReviewerFilter] = useState('All');
  const [coverStatusFilter, setCoverStatusFilter] = useState('All');

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };

  const fetchAll = useCallback(async () => {
    try {
      const [booksRes, coversRes, reviewersRes] = await Promise.all([
        fetch(`${API_BASE}/api/review-queue`, { credentials: 'omit', headers }),
        fetch(`${API_BASE}/api/covers/review-queue`, { credentials: 'omit', headers }),
        fetch(`${API_BASE}/api/admin/reviewers`, { credentials: 'omit', headers }),
      ]);
      setPendingBooks(booksRes.ok ? await booksRes.json() : []);
      setPendingCovers(coversRes.ok ? await coversRes.json() : []);
      setReviewers(reviewersRes.ok ? await reviewersRes.json() : []);
    } catch (e) {
      console.error('[LeadReviewerPanel] fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Assignment handlers ────────────────────────────
  const handleAssignCover = async (coverId, reviewerId) => {
    setAssigning(coverId);
    try {
      const res = await fetch(`${API_BASE}/api/covers/${coverId}/assign`, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify({ reviewerId: reviewerId || null }),
      });
      if (res.ok) await fetchAll();
      else alert('Failed to assign cover');
    } catch { alert('Server error'); }
    finally { setAssigning(null); }
  };

  const handleAssignDraft = async (draftId, reviewerId) => {
    setAssigning(draftId);
    try {
      const res = await fetch(`${API_BASE}/api/review/${draftId}/assign`, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify({ reviewerId: reviewerId || null }),
      });
      if (res.ok) await fetchAll();
      else alert('Failed to assign draft');
    } catch { alert('Server error'); }
    finally { setAssigning(null); }
  };

  // ─── Cover review handlers ──────────────────────────
  const handleApproveCover = async (coverId) => {
    setIsProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/api/covers/${coverId}/approve`, {
        method: 'POST', credentials: 'omit', headers,
      });
      if (res.ok) { setPendingCovers(prev => prev.filter(c => c._id !== coverId)); setSelectedCover(null); alert('Cover approved!'); }
      else alert('Failed to approve cover');
    } catch { alert('Server error'); }
    finally { setIsProcessing(false); }
  };

  const handleRejectCover = async (coverId) => {
    setIsProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/api/covers/${coverId}/reject`, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify({ notes: rejectNotes }),
      });
      if (res.ok) { setPendingCovers(prev => prev.filter(c => c._id !== coverId)); setSelectedCover(null); setRejectNotes(''); alert('Cover rejected.'); }
      else alert('Failed to reject cover');
    } catch { alert('Server error'); }
    finally { setIsProcessing(false); }
  };

  const handleApproveDraft = async (bookId) => {
    try {
      const res = await fetch(`${API_BASE}/api/review/${bookId}/approve`, {
        method: 'POST', credentials: 'omit', headers,
      });
      if (res.ok) { setPendingBooks(prev => prev.filter(b => b._id !== bookId)); alert('Book approved!'); }
      else alert('Failed to approve book');
    } catch { alert('Server error'); }
  };

  const handleRejectDraft = async (bookId, notes) => {
    try {
      const res = await fetch(`${API_BASE}/api/review/${bookId}/reject`, {
        method: 'POST', credentials: 'omit', headers,
        body: JSON.stringify({ notes }),
      });
      if (res.ok) { setPendingBooks(prev => prev.filter(b => b._id !== bookId)); alert('Book rejected.'); }
      else alert('Failed to reject book');
    } catch { alert('Server error'); }
  };

  // ─── Helpers ────────────────────────────────────────
  const computePdfUrl = (b) => `${API_BASE}/api/drafts/${b._id}/pdf`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  const unassignedCovers = pendingCovers.filter(c => !c.assignedTo);
  const assignedCovers   = pendingCovers.filter(c => c.assignedTo);
  const unassignedDrafts = pendingBooks.filter(b => !b.assignedTo);
  const assignedDrafts   = pendingBooks.filter(b => b.assignedTo);

  // ─── Filter options (derived from data) ─────────────
  const bookAuthors   = useMemo(() => ['All', ...new Set(pendingBooks.map(b => b.authorName).filter(Boolean))], [pendingBooks]);
  const coverDesigners = useMemo(() => ['All', ...new Set(pendingCovers.map(c => c.authorName || c.designerName).filter(Boolean))], [pendingCovers]);
  const reviewerNames = useMemo(() => ['All', ...reviewers.map(r => r.username)], [reviewers]);

  // ─── Filtered data ───────────────────────────────────
  const filteredBooks = useMemo(() => {
    const term = bookSearch.trim().toLowerCase();
    return pendingBooks.filter(b => {
      const matchSearch = !term ||
        (b.title || '').toLowerCase().includes(term) ||
        (b.authorName || '').toLowerCase().includes(term) ||
        (b._id || '').toLowerCase().includes(term);
      const matchAuthor   = bookAuthorFilter === 'All' || b.authorName === bookAuthorFilter;
      const matchReviewer = bookReviewerFilter === 'All' || b.assignedUsername === bookReviewerFilter;
      const matchStatus   = bookStatusFilter === 'All' ||
        (bookStatusFilter === 'Unassigned' && !b.assignedTo) ||
        (bookStatusFilter === 'Assigned' && !!b.assignedTo);
      return matchSearch && matchAuthor && matchReviewer && matchStatus;
    });
  }, [pendingBooks, bookSearch, bookAuthorFilter, bookReviewerFilter, bookStatusFilter]);

  const filteredCovers = useMemo(() => {
    const term = coverSearch.trim().toLowerCase();
    return pendingCovers.filter(c => {
      const designer = c.authorName || c.designerName || '';
      const matchSearch = !term ||
        (c.title || '').toLowerCase().includes(term) ||
        designer.toLowerCase().includes(term) ||
        (c._id || '').toLowerCase().includes(term);
      const matchDesigner = coverDesignerFilter === 'All' || designer === coverDesignerFilter;
      const matchReviewer = coverReviewerFilter === 'All' || c.assignedUsername === coverReviewerFilter;
      const matchStatus   = coverStatusFilter === 'All' ||
        (coverStatusFilter === 'Unassigned' && !c.assignedTo) ||
        (coverStatusFilter === 'Assigned' && !!c.assignedTo);
      return matchSearch && matchDesigner && matchReviewer && matchStatus;
    });
  }, [pendingCovers, coverSearch, coverDesignerFilter, coverReviewerFilter, coverStatusFilter]);

  if (loading) return <CircularProgress />;

  return (
    <>
      <h2 className="section-title">Lead Reviewer Dashboard</h2>

      {/* Stats bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={`${pendingCovers.length} covers pending`} color="primary" variant="outlined" />
        <Chip label={`${pendingBooks.length} books pending`} color="primary" variant="outlined" />
        <Chip label={`${unassignedCovers.length + unassignedDrafts.length} unassigned`} color="warning" variant="outlined" />
        <Chip label={`${reviewers.length} reviewers available`} color="success" variant="outlined" />
        <Box sx={{ ml: 'auto' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="cards"><ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />Cards</ToggleButton>
            <ToggleButton value="table"><TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />Table</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {viewMode === 'cards' ? (
        // ════════════════════════════════════════════
        // CARDS VIEW (original layout)
        // ════════════════════════════════════════════
        <>
          {/* Unassigned Covers */}
          <div className="section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#e53e3e' }}>📋 Unassigned Covers ({unassignedCovers.length})</h3>
            {unassignedCovers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">All covers have been assigned.</Typography>
            ) : (
              <div className="cover-grid">
                {unassignedCovers.map(cover => (
                  <Box key={cover._id} sx={{ position: 'relative' }}>
                    <CoverCard coverId={cover._id} title={cover.title} authorName={cover.authorName} coverUrl={cover.cover_url} onClick={() => setSelectedCover(cover)} />
                    <AssignDropdown reviewers={reviewers} currentAssignee={null} disabled={assigning === cover._id} onAssign={(id) => handleAssignCover(cover._id, id)} />
                  </Box>
                ))}
              </div>
            )}
          </div>

          {/* Assigned Covers */}
          {assignedCovers.length > 0 && (
            <div className="section" style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#38a169' }}>✅ Assigned Covers ({assignedCovers.length})</h3>
              <div className="cover-grid">
                {assignedCovers.map(cover => (
                  <Box key={cover._id} sx={{ position: 'relative' }}>
                    <CoverCard coverId={cover._id} title={cover.title} authorName={cover.authorName} coverUrl={cover.cover_url} onClick={() => setSelectedCover(cover)} />
                    <AssignDropdown reviewers={reviewers} currentAssignee={cover.assignedTo} assignedUsername={cover.assignedUsername} disabled={assigning === cover._id} onAssign={(id) => handleAssignCover(cover._id, id)} />
                  </Box>
                ))}
              </div>
            </div>
          )}

          {/* Unassigned Drafts */}
          <div className="section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#e53e3e' }}>📋 Unassigned Books ({unassignedDrafts.length})</h3>
            {unassignedDrafts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">All books have been assigned.</Typography>
            ) : (
              <div className="tile-grid">
                {unassignedDrafts.map(book => (
                  <Box key={book._id}>
                    <DashboardCard title={book.title} genre={book.genre} volume={book.volume} role="lead_reviewer"
                      openTo={`/review/${book._id}`} openState={{ book: { _id: book._id, title: book.title, pdfUrl: computePdfUrl(book) } }} />
                    <AssignDropdown reviewers={reviewers} currentAssignee={null} disabled={assigning === book._id} onAssign={(id) => handleAssignDraft(book._id, id)} />
                  </Box>
                ))}
              </div>
            )}
          </div>

          {/* Assigned Drafts */}
          {assignedDrafts.length > 0 && (
            <div className="section" style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#38a169' }}>✅ Assigned Books ({assignedDrafts.length})</h3>
              <div className="tile-grid">
                {assignedDrafts.map(book => (
                  <Box key={book._id}>
                    <DashboardCard title={book.title} genre={book.genre} volume={book.volume} role="lead_reviewer"
                      openTo={`/review/${book._id}`} openState={{ book: { _id: book._id, title: book.title, pdfUrl: computePdfUrl(book) } }} />
                    <AssignDropdown reviewers={reviewers} currentAssignee={book.assignedTo} assignedUsername={book.assignedUsername} disabled={assigning === book._id} onAssign={(id) => handleAssignDraft(book._id, id)} />
                  </Box>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        // ════════════════════════════════════════════
        // TABLE VIEW
        // ════════════════════════════════════════════
        <>
          {/* ── Books Table ── */}
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>📚 Manuscripts</Typography>

          {/* Books filters */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" placeholder="Search title, author, ID..." value={bookSearch}
              onChange={e => setBookSearch(e.target.value)} sx={{ minWidth: 260 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Author</InputLabel>
              <Select value={bookAuthorFilter} label="Author" onChange={e => setBookAuthorFilter(e.target.value)}>
                {bookAuthors.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Assigned Reviewer</InputLabel>
              <Select value={bookReviewerFilter} label="Assigned Reviewer" onChange={e => setBookReviewerFilter(e.target.value)}>
                {reviewerNames.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select value={bookStatusFilter} label="Status" onChange={e => setBookStatusFilter(e.target.value)}>
                {['All', 'Unassigned', 'Assigned'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Author</strong></TableCell>
                  <TableCell><strong>Genre</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell><strong>Assigned To</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBooks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No manuscripts match your filters.</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredBooks.map(book => (
                  <TableRow key={book._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b' }}>
                      <Tooltip title={book._id}><span>{book._id?.slice(-8)}</span></Tooltip>
                    </TableCell>
                    <TableCell>{book.title || '—'}</TableCell>
                    <TableCell>{book.authorName || '—'}</TableCell>
                    <TableCell>{book.genre || '—'}</TableCell>
                    <TableCell>{formatDate(book.lastEdited)}</TableCell>
                    <TableCell>
                      <AssignDropdown reviewers={reviewers} currentAssignee={book.assignedTo} assignedUsername={book.assignedUsername}
                        disabled={assigning === book._id} onAssign={(id) => handleAssignDraft(book._id, id)} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={book.assignedTo ? 'Assigned' : 'Unassigned'}
                        color={book.assignedTo ? 'success' : 'warning'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" href={`/review/${book._id}?type=draft`} target="_blank">Open</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Covers Table ── */}
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>🎨 Covers</Typography>

          {/* Covers filters */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" placeholder="Search title, designer, ID..." value={coverSearch}
              onChange={e => setCoverSearch(e.target.value)} sx={{ minWidth: 260 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Designer</InputLabel>
              <Select value={coverDesignerFilter} label="Designer" onChange={e => setCoverDesignerFilter(e.target.value)}>
                {coverDesigners.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Assigned Reviewer</InputLabel>
              <Select value={coverReviewerFilter} label="Assigned Reviewer" onChange={e => setCoverReviewerFilter(e.target.value)}>
                {reviewerNames.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select value={coverStatusFilter} label="Status" onChange={e => setCoverStatusFilter(e.target.value)}>
                {['All', 'Unassigned', 'Assigned'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Designer</strong></TableCell>
                  <TableCell><strong>Cover Type</strong></TableCell>
                  <TableCell><strong>Submitted</strong></TableCell>
                  <TableCell><strong>Assigned To</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCovers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No covers match your filters.</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredCovers.map(cover => (
                  <TableRow key={cover._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b' }}>
                      <Tooltip title={cover._id}><span>{cover._id?.slice(-8)}</span></Tooltip>
                    </TableCell>
                    <TableCell>{cover.title || '—'}</TableCell>
                    <TableCell>{cover.authorName || cover.designerName || '—'}</TableCell>
                    <TableCell>{cover.coverType || '—'}</TableCell>
                    <TableCell>{formatDate(cover.lastEdited || cover.submittedAt)}</TableCell>
                    <TableCell>
                      <AssignDropdown reviewers={reviewers} currentAssignee={cover.assignedTo} assignedUsername={cover.assignedUsername}
                        disabled={assigning === cover._id} onAssign={(id) => handleAssignCover(cover._id, id)} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={cover.assignedTo ? 'Assigned' : 'Unassigned'}
                        color={cover.assignedTo ? 'success' : 'warning'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => setSelectedCover(cover)}>Review</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Cover Preview Modal ─────────────────── */}
      <Modal open={!!selectedCover} onClose={() => { setSelectedCover(null); setRejectNotes(''); }}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500, maxWidth: '95vw', maxHeight: '90vh',
          bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 3, overflow: 'auto',
        }}>
          {selectedCover && (
            <>
              <Typography variant="h6" gutterBottom>Review Cover</Typography>
              <img src={selectedCover.cover_url} alt={`Cover for ${selectedCover.title}`}
                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1rem' }} />
              <Typography variant="subtitle1" fontWeight={600}>{selectedCover.title}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>by {selectedCover.authorName}</Typography>
              {selectedCover.assignedUsername && (
                <Chip label={`Assigned to: ${selectedCover.assignedUsername}`} size="small" color="info" sx={{ mb: 1 }} />
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={() => setSelectedCover(null)}>Close</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

// ─── Assignment dropdown sub-component ─────────────
function AssignDropdown({ reviewers, currentAssignee, assignedUsername, disabled, onAssign }) {
  const [value, setValue] = useState(currentAssignee || '');

  useEffect(() => { setValue(currentAssignee || ''); }, [currentAssignee]);

  const handleChange = (e) => {
    const newVal = e.target.value;
    setValue(newVal);
    onAssign(newVal || null);
  };

  return (
    <FormControl fullWidth size="small" sx={{ mt: 1 }} disabled={disabled}>
      <InputLabel>
        {currentAssignee
          ? <Tooltip title="Reassign"><span>Assigned: {assignedUsername}</span></Tooltip>
          : 'Assign to reviewer'}
      </InputLabel>
      <Select value={value} onChange={handleChange}
        label={currentAssignee ? `Assigned: ${assignedUsername}` : 'Assign to reviewer'}>
        <MenuItem value=""><em>{currentAssignee ? 'Unassign' : '— Select —'}</em></MenuItem>
        {reviewers.map(r => <MenuItem key={r.id} value={r.id}>{r.username}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

export default LeadReviewerPanel;
